import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import './blogs.css';
import BlogStickySignup from '../../components/BlogStickySignup';
import { fetchBlogPost } from '../../api/blog';
import { decorateBlogArticleHtml, stripMarkdownFrontmatter } from './blogMarkdown';

const { ogImageUrlForPath } = require('../../site/ogTitleCard');

function BlogLoadingSpinner({ label }) {
  return (
    <div className="blog-loading" role="status" aria-label={label}>
      <Spinner animation="border" variant="light" />
    </div>
  );
}

function BlogUnavailableState() {
  return (
    <section className="blog-empty-state blog-empty-state--error">
      <div className="blog-empty-card">
        <i className="blog-empty-icon fas fa-exclamation-circle" aria-hidden="true" />
        <h2 className="blog-empty-title">Article not found</h2>
        <p className="blog-empty-text">This article is unavailable or has not been published yet.</p>
      </div>
    </section>
  );
}

function hideBrokenArticleImage(event) {
  const image = event.target;

  if (!image || image.tagName !== 'IMG') return;

  image.style.display = 'none';
  image.setAttribute('aria-hidden', 'true');

  const parent = image.parentElement;
  if (parent?.tagName === 'P' && !parent.textContent.trim()) {
    parent.style.display = 'none';
    parent.setAttribute('aria-hidden', 'true');
  }
}

const BOTSPOT_URL = 'https://botspot.trade';
const BOTSPOT_LOGO_URL = `${BOTSPOT_URL}/logo512.png`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let active = true;
    setStatus('loading');
    setPost(null);
    fetchBlogPost(slug)
      .then((item) => {
        if (!active) return;
        setPost(item);
        setStatus('ready');
      })
      .catch(() => {
        if (!active) return;
        setStatus('error');
      });
    return () => {
      active = false;
    };
  }, [slug]);

  const articleHtml = useMemo(() => {
    if (post?.html) return decorateBlogArticleHtml(DOMPurify.sanitize(post.html));
    if (!post?.markdown) return '';
    return decorateBlogArticleHtml(DOMPurify.sanitize(marked.parse(stripMarkdownFrontmatter(post.markdown))));
  }, [post]);

  if (status === 'loading') {
    return (
      <main className="blog-post-dynamic">
        <Helmet>
          <title>Loading article | BotSpot</title>
        </Helmet>
        <Link className="blog-back-link" to="/blog">Blog</Link>
        <h1>Latest BotSpot article</h1>
        <BlogLoadingSpinner label="Loading article" />
      </main>
    );
  }

  if (status === 'error' || !post) {
    return (
      <main className="blog-post-dynamic">
        <Helmet>
          <title>Article not found | BotSpot</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Link className="blog-back-link" to="/blog">Blog</Link>
        <h1>Latest BotSpot article</h1>
        <BlogUnavailableState />
      </main>
    );
  }

  const publishedDate = post.publishedAt || post.createdAt;
  const canonicalUrl = post.canonicalUrl || `${BOTSPOT_URL}/blog/${post.slug}`;
  const seoDescription = post.description || post.subtitle || '';
  const seoImage = post.heroImageUrl || ogImageUrlForPath(`/blog/${post.slug}`);

  return (
    <main className="blog-post-dynamic" onErrorCapture={hideBrokenArticleImage}>
      <Helmet>
        <title>{post.title}</title>
        {seoDescription && <meta name="description" content={seoDescription} />}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={post.title} />
        {seoDescription && <meta property="og:description" content={seoDescription} />}
        <meta property="og:image" content={seoImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        {seoDescription && <meta name="twitter:description" content={seoDescription} />}
        <meta name="twitter:image" content={seoImage} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: seoDescription,
          author: { '@type': 'Organization', name: post.authorName || 'BotSpot' },
          publisher: {
            '@type': 'Organization',
            name: 'BotSpot',
            url: BOTSPOT_URL,
            logo: { '@type': 'ImageObject', url: BOTSPOT_LOGO_URL },
          },
          datePublished: publishedDate,
          dateModified: post.updatedAt || publishedDate,
          image: seoImage ? [seoImage] : undefined,
          url: canonicalUrl,
          mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
        })}</script>
      </Helmet>

      <BlogStickySignup title="Try AI Trading Bot Builder" />

      <Link className="blog-back-link" to="/blog">Blog</Link>
      <h1>{post.title}</h1>
      <div className="blog-post-meta">
        <div className="blog-post-author-info">
          <strong>{post.authorName || 'The BotSpot Team'}</strong>
          {publishedDate && (
            <time className="blog-post-date" dateTime={publishedDate}>
              {new Date(publishedDate).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </time>
          )}
        </div>
      </div>

      {post.heroImageUrl && (
        <p>
          <img
            className="blog-post-hero-image"
            src={post.heroImageUrl}
            alt={post.heroImageAlt || `${post.title} article cover`}
          />
        </p>
      )}

      <div className="post-content" dangerouslySetInnerHTML={{ __html: articleHtml }} />

      <nav className="blog-related-links" aria-label="Explore BotSpot">
        <strong>Keep exploring</strong>
        <Link to="/marketplace">Browse trading strategies</Link>
        <Link to="/case-studies">Read trading case studies</Link>
        <Link to="/agents">Connect your AI assistant</Link>
      </nav>
    </main>
  );
}
